
// Central bilingual content store. Every user-facing string lives here.
// Edit values freely — counters and contact details are meant to be updated.

export type Lang = "es" | "en";

export const translations = {
  es: {
    nav: {
      inicio: "Inicio",
      nosotros: "Nosotros",
      productos: "Productos",
      capacidad: "Capacidad Operativa",
      galeria: "Galería",
      contacto: "Contacto",
      cotizar: "Solicitar Cotización",
    },
    hero: {
      eyebrow: "Granzonera y Agregados San Antonio, C.A.",
      headline: "Produciendo materiales de construcción con calidad, confianza y compromiso.",
      subtitle:
        "Somos especialistas en la producción y suministro de agregados para la construcción, ofreciendo materiales de alta calidad para proyectos residenciales, comerciales e industriales en toda Venezuela.",
      location: "Jalisco, Trujillo · Venezuela",
      ctaPrimary: "Solicitar Cotización",
      ctaSecondary: "Contáctanos",
    },
    about: {
      eyebrow: "Nosotros",
      title: "Un proveedor confiable de agregados para la construcción",
      p1: "En GRANSACA producimos y comercializamos agregados de piedra triturada bajo una operación enfocada en la calidad, la eficiencia operativa, la seguridad y la satisfacción del cliente.",
      p2: "Atendemos a contratistas, desarrolladores, empresas constructoras y proyectos de infraestructura en toda Venezuela, garantizando un suministro constante y materiales que cumplen con los más altos estándares de la industria.",
      stats: [
        { value: "Suministro", label: "Constante y confiable" },
        { value: "Calidad", label: "Garantizada en cada despacho" },
      ],
      missionTitle: "Nuestra Misión",
      mission:
        "Producir y suministrar agregados de la más alta calidad con responsabilidad y excelencia operativa, garantizando la satisfacción de nuestros clientes mediante un suministro confiable y la mejora continua de nuestros procesos.",
      visionTitle: "Nuestra Visión",
      vision:
        "Consolidarnos como uno de los principales proveedores de agregados de Venezuela, impulsados por la innovación, el crecimiento sostenible y la excelencia operativa en cada proyecto que atendemos.",
    },
    products: {
      eyebrow: "Nuestros Productos",
      title: "Agregados para cada proyecto",
      subtitle:
        "Materiales pétreos producidos bajo estrictos controles de calidad para construcción residencial, comercial e industrial.",
      cta: "Solicitar Información",
      more: "Y más...",
      moreDesc: "Consulta por materiales específicos según los requerimientos de tu proyecto.",
      items: [
        { name: "Arena", desc: "Arena lavada de granulometría fina, ideal para mezclas de concreto y morteros." },
        { name: "Polvillo", desc: "Fino de trituración empleado en bases, mezclas asfálticas y compactación." },
        { name: "Piedra 3/4", desc: "Piedra triturada de 3/4\", ideal para concreto estructural de alta resistencia." },
        { name: "Piedra 7/8", desc: "Piedra triturada de 7/8\", indicada para bases y concretos de mayor granulometría." },
        { name: "Granzón", desc: "Agregado grueso mixto para relleno estructural, bases y estabilización de suelos." },
        { name: "Base", desc: "Material granular de base para vialidades, pavimentos y explanaciones." },
        { name: "Material de Relleno", desc: "Material de relleno para nivelación, terraplenes y obras de movimiento de tierra." },
      ],
    },
    capacity: {
      eyebrow: "Capacidad Operativa",
      title: "Una operación sólida y capaz",
      subtitle:
        "Infraestructura, maquinaria y procesos diseñados para responder a proyectos de cualquier escala.",
      items: [
        { value: "Producción", label: "Producción de agregados" },
        { value: "Maquinaria", label: "Maquinaria pesada" },
        { value: "Planta", label: "Planta trituradora moderna" },
        { value: "Despachos", label: "Despachos para proyectos grandes y pequeños" },
        { value: "Constructoras", label: "Atención a empresas constructoras" },
        { value: "Calidad", label: "Calidad garantizada" },
        { value: "Inventario", label: "Disponibilidad de inventario" },
        { value: "Servicio", label: "Servicio confiable" },
      ],
    },
    why: {
      eyebrow: "Por qué elegirnos",
      title: "Respaldo que da confianza",
      items: [
        { title: "Calidad Garantizada", desc: "Materiales producidos bajo estrictos estándares de calidad." },
        { title: "Producción Eficiente", desc: "Procesos optimizados para un suministro constante." },
        { title: "Entregas Confiables", desc: "Despachos puntuales para proyectos de cualquier tamaño." },
        { title: "Atención Personalizada", desc: "Asesoría cercana en cada etapa de tu proyecto." },
      ],
    },
    machinery: {
      eyebrow: "Maquinaria",
      title: "Equipos modernos para una producción de alto rendimiento",
      subtitle:
        "Operamos maquinaria moderna para garantizar la eficiencia de producción y la calidad de nuestros materiales.",
      list: [
        "Cargadores frontales (Wheel Loaders)",
        "Excavadoras",
        "Planta trituradora",
        "Camiones volteo (Dump Trucks)",
        "Sistemas de transporte (Conveyors)",
        "Equipos industriales",
      ],
    },
    safety: {
      eyebrow: "Seguridad y Calidad",
      safetyTitle: "Compromiso con la seguridad y el medio ambiente",
      safetyItems: [
        "Compromiso con la seguridad industrial",
        "Protección del medio ambiente",
        "Responsabilidad operacional",
        "Cumplimiento de estándares de calidad",
      ],
      qualityTitle: "Calidad en cada material",
      qualityText:
        "Cada material se produce bajo estrictos estándares de calidad para asegurar consistencia y confiabilidad en los proyectos de construcción, respaldando el rendimiento de nuestros agregados en cada despacho.",
    },
    gallery: {
      eyebrow: "Galería",
      title: "Nuestra operación en imágenes",
      subtitle: "Planta trituradora, acopios, cargas, maquinaria y procesos de producción.",
    },
    cta: {
      headline: "¿Necesitas materiales para tu próximo proyecto?",
      subtitle:
        "Nuestro equipo está listo para ayudarte con materiales de calidad y atención personalizada.",
      button: "Solicitar Cotización",
    },
    contact: {
      eyebrow: "Contacto",
      title: "Hablemos de tu proyecto",
      subtitle:
        "Completa el formulario o contáctanos directamente. Te responderemos a la brevedad.",
      companyLabel: "Empresa",
      company: "Granzonera y Agregados San Antonio, C.A.",
      phoneLabel: "Teléfono",
      whatsappLabel: "WhatsApp",
      emailLabel: "Correo",
      addressLabel: "Dirección",
      address: "Carretera Nacional, Sector San Antonio, Venezuela",
      hoursLabel: "Horario de Atención",
      hours: "Lunes a Viernes: 7:00 AM – 5:00 PM · Sábados: 7:00 AM – 12:00 PM",
      form: {
        name: "Nombre",
        email: "Correo",
        phone: "Teléfono",
        company: "Empresa (opcional)",
        message: "Mensaje",
        submit: "Enviar Mensaje",
        success: "¡Gracias! Hemos recibido tu mensaje y te contactaremos pronto.",
      },
    },
    footer: {
      tagline:
        "Producción y comercialización de agregados para la construcción en toda Venezuela.",
      quickLinks: "Enlaces Rápidos",
      productsTitle: "Productos",
      companyTitle: "Empresa",
      contactTitle: "Contacto",
      rights: "Todos los derechos reservados.",
    },
  },
  en: {
    nav: {
      inicio: "Home",
      nosotros: "About Us",
      productos: "Products",
      capacidad: "Operational Capacity",
      galeria: "Gallery",
      contacto: "Contact",
      cotizar: "Request a Quote",
    },
    hero: {
      eyebrow: "Granzonera y Agregados San Antonio, C.A.",
      headline: "Producing construction materials with quality, trust and commitment.",
      subtitle:
        "We specialize in the production and supply of construction aggregates, delivering high-quality materials for residential, commercial and industrial projects throughout Venezuela.",
      location: "Jalisco, Trujillo · Venezuela",
      ctaPrimary: "Request a Quote",
      ctaSecondary: "Contact Us",
    },
    about: {
      eyebrow: "About Us",
      title: "A reliable supplier of construction aggregates",
      p1: "At GRANSACA we produce and market crushed stone aggregates through an operation focused on quality, operational efficiency, safety and customer satisfaction.",
      p2: "We serve contractors, developers, construction companies and infrastructure projects throughout Venezuela, ensuring a consistent supply and materials that meet the highest industry standards.",
      stats: [
        { value: "Supply", label: "Consistent and reliable" },
        { value: "Quality", label: "Guaranteed in every dispatch" },
      ],
      missionTitle: "Our Mission",
      mission:
        "To produce and supply the highest quality aggregates with responsibility and operational excellence, ensuring customer satisfaction through reliable supply and continuous improvement of our processes.",
      visionTitle: "Our Vision",
      vision:
        "To establish ourselves as one of Venezuela's leading aggregate suppliers, driven by innovation, sustainable growth and operational excellence in every project we serve.",
    },
    products: {
      eyebrow: "Our Products",
      title: "Aggregates for every project",
      subtitle:
        "Stone materials produced under strict quality controls for residential, commercial and industrial construction.",
      cta: "Request Information",
      more: "And more...",
      moreDesc: "Ask about specific materials according to your project requirements.",
      items: [
        { name: "Sand", desc: "Washed fine-grain sand, ideal for concrete mixes and mortars." },
        { name: "Stone Dust", desc: "Crushing fines used in bases, asphalt mixes and compaction." },
        { name: "3/4\" Stone", desc: "3/4\" crushed stone, ideal for high-strength structural concrete." },
        { name: "7/8\" Stone", desc: "7/8\" crushed stone, suited for bases and larger-grain concrete." },
        { name: "Granzón", desc: "Mixed coarse aggregate for structural fill, bases and soil stabilization." },
        { name: "Base Material", desc: "Granular base material for roads, pavements and grading." },
        { name: "Fill Material", desc: "Fill material for leveling, embankments and earthworks." },
      ],
    },
    capacity: {
      eyebrow: "Operational Capacity",
      title: "A solid and capable operation",
      subtitle: "Infrastructure, machinery and processes built to serve projects of any scale.",
      items: [
        { value: "Production", label: "Aggregate production" },
        { value: "Machinery", label: "Heavy machinery" },
        { value: "Plant", label: "Modern crushing plant" },
        { value: "Dispatch", label: "Deliveries for large and small projects" },
        { value: "Builders", label: "Service to construction companies" },
        { value: "Quality", label: "Guaranteed quality" },
        { value: "Inventory", label: "Inventory availability" },
        { value: "Service", label: "Reliable service" },
      ],
    },
    why: {
      eyebrow: "Why choose us",
      title: "The backing that builds confidence",
      items: [
        { title: "Guaranteed Quality", desc: "Materials produced under strict quality standards." },
        { title: "Efficient Production", desc: "Optimized processes for a consistent supply." },
        { title: "Reliable Deliveries", desc: "On-time dispatch for projects of any size." },
        { title: "Personalized Service", desc: "Close support at every stage of your project." },
      ],
    },
    machinery: {
      eyebrow: "Machinery",
      title: "Modern equipment for high-performance production",
      subtitle:
        "We operate modern machinery to guarantee production efficiency and the quality of our materials.",
      list: [
        "Wheel Loaders",
        "Excavators",
        "Crusher Plant",
        "Dump Trucks",
        "Conveyors",
        "Industrial Equipment",
      ],
    },
    safety: {
      eyebrow: "Safety & Quality",
      safetyTitle: "Commitment to safety and the environment",
      safetyItems: [
        "Commitment to industrial safety",
        "Environmental protection",
        "Operational responsibility",
        "Compliance with quality standards",
      ],
      qualityTitle: "Quality in every material",
      qualityText:
        "Every material is produced under strict quality standards to ensure consistency and reliability for construction projects, backing the performance of our aggregates in every dispatch.",
    },
    gallery: {
      eyebrow: "Gallery",
      title: "Our operation in pictures",
      subtitle: "Crushing plant, stockpiles, loading, machinery and production processes.",
    },
    cta: {
      headline: "Need materials for your next project?",
      subtitle: "Our team is ready to help you with quality materials and personalized service.",
      button: "Request a Quote",
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's talk about your project",
      subtitle: "Fill out the form or reach us directly. We'll get back to you shortly.",
      companyLabel: "Company",
      company: "Granzonera y Agregados San Antonio, C.A.",
      phoneLabel: "Phone",
      whatsappLabel: "WhatsApp",
      emailLabel: "Email",
      addressLabel: "Address",
      address: "Carretera Nacional, Sector San Antonio, Venezuela",
      hoursLabel: "Business Hours",
      hours: "Monday to Friday: 7:00 AM – 5:00 PM · Saturday: 7:00 AM – 12:00 PM",
      form: {
        name: "Name",
        email: "Email",
        phone: "Phone",
        company: "Company (optional)",
        message: "Message",
        submit: "Send Message",
        success: "Thank you! We've received your message and will contact you soon.",
      },
    },
    footer: {
      tagline: "Production and marketing of construction aggregates throughout Venezuela.",
      quickLinks: "Quick Links",
      productsTitle: "Products",
      companyTitle: "Company",
      contactTitle: "Contact",
      rights: "All rights reserved.",
    },
  },
} as const;

// Static company contact data (shared across languages) — edit freely.
export const company = {
  name: "GRANSACA.COM",
  legalName: "Granzonera y Agregados San Antonio, C.A.",
  website: "GRANSACA.COM",
  phone: "+58 424 000 0000",
  whatsapp: "+58 424 000 0000",
  whatsappLink: "https://wa.me/584240000000",
  email: "info@gransaca.com",
  mapUrl: "https://maps.app.goo.gl/vfrNqdBPeuGiP6eq6",
  mapEmbed: "https://www.google.com/maps?q=9.440105,-70.580570&z=16&output=embed",
};

export const images = {
  hero: "https://cdn.magicpatterns.com/patterns/generated-images/ed97d09c-5468-4e7f-9a71-cf4a21343b76.jpg",
  about: "https://cdn.magicpatterns.com/patterns/generated-images/64bd64dc-a733-453d-a36b-4075e4ba5dee.jpg",
  machinery: "https://cdn.magicpatterns.com/patterns/generated-images/32aaeb41-11a7-4579-ba03-2aaf7f27a9dc.jpg",
  cta: "https://cdn.magicpatterns.com/patterns/generated-images/5bfb3198-8531-40e0-bd91-cd77a0004893.jpg",
  products: [
    "https://cdn.magicpatterns.com/patterns/generated-images/f0636483-da81-4a19-b23e-739a00886cff.jpg",
    "https://cdn.magicpatterns.com/patterns/generated-images/e00cbb7b-661c-4047-9a84-e437299f4301.jpg",
    "https://cdn.magicpatterns.com/patterns/generated-images/516731be-5f1b-4bf7-8ce9-7d52507aee62.jpg",
    "https://cdn.magicpatterns.com/patterns/generated-images/f2a13467-e701-4a22-a37a-5efd1a19a443.jpg",
    "https://cdn.magicpatterns.com/patterns/generated-images/0e5be3ad-dc3c-44c9-a1d6-eaf05fd2219c.jpg",
    "https://cdn.magicpatterns.com/patterns/generated-images/516731be-5f1b-4bf7-8ce9-7d52507aee62.jpg",
    "https://cdn.magicpatterns.com/patterns/generated-images/f0636483-da81-4a19-b23e-739a00886cff.jpg",
  ],
  gallery: [
    "https://cdn.magicpatterns.com/patterns/generated-images/f6c422a0-0919-4518-a327-c2bb9e74a1eb.jpg",
    "https://cdn.magicpatterns.com/patterns/generated-images/9dc2db24-fd80-41e2-a02f-cd27109eab7b.jpg",
    "https://cdn.magicpatterns.com/patterns/generated-images/a9e5235d-6284-40ee-869c-b0ad637c374f.jpg",
    "https://cdn.magicpatterns.com/patterns/generated-images/7aca1473-8f3c-4d5e-afb3-a6235ba471e6.jpg",
    "https://cdn.magicpatterns.com/patterns/generated-images/3c472292-1345-4c9a-94b2-aecc72dfb79a.jpg",
    "https://cdn.magicpatterns.com/patterns/generated-images/32aaeb41-11a7-4579-ba03-2aaf7f27a9dc.jpg",
  ],
  logo: "https://cdn.magicpatterns.com/uploads/5FXHHcc1L5JJ71BCNmRT6D/image.png",
};

